// ClientsConfig.tsx
import React, { useState, useEffect } from 'react';
import { useClientStore } from '@/store/clientStore';
import { AddClientModal } from '@/components/modals/AddClientModal';
import { EditClientModal } from '@/components/modals/EditClientModal';
import { Edit2, Trash2 } from 'lucide-react';
import { Client } from '@/types';

export const ClientsConfig: React.FC = () => {
  const { 
    clients = [],
    loading, 
    pagination, 
    fetchClients, 
    deleteClient,
  } = useClientStore();
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);

  useEffect(() => {
    fetchClients({
      page: currentPage,
      search: searchTerm,
      limit: 20,
      isActive: true 
    });
  }, [currentPage, searchTerm]);

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      await deleteClient(id);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedClients(clients.map(client => client._id || client.id || ''));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (clientId: string) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const clientsList = Array.isArray(clients) ? clients : [];

  return (
    <div className="h-full bg-white mx-0 my-0">
      <div className="h-full flex flex-col">
        <div className="overflow-auto flex-1">
         <table className="w-full border border-gray-200 border-collapse">

            <thead>
              <tr className="border border-gray-200">
                <th className="text-left bg-gray-100 px-4 border py-1 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300"
                    onChange={handleSelectAll}
                    checked={selectedClients.length === clientsList.length && clientsList.length > 0}
                  />
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  CLIENT
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  EMAIL ADDRESS
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  PHONE NUMBER
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  ADDRESS
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  GST NUMBER
                </th>
                <th className="w-20 bg-gray-100"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    Loading clients...
                  </td>
                </tr>
              ) : clientsList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No clients found
                  </td>
                </tr>
              ) : (
                clientsList.map((client) => (
                  <tr key={client._id || client.id} className="border border-gray-100 hover:bg-gray-50">
                    <td className="px-4 border py-1">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300"
                        checked={selectedClients.includes(client._id || client.id || '')}
                        onChange={() => handleSelectClient(client._id || client.id || '')}
                      />
                    </td>
                    <td className="px-4 border py-1 text-sm font-medium text-gray-900">
                      {client.name}
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {client.email || '-'}
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {client.phone || '-'}
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {client.address || '-'}
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {client.gst || '-'}
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          onClick={() => setEditingClient(client)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          onClick={() => handleDelete(client._id || client.id || '', client.name)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 border py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600 uppercase">
            COUNT <span className="ml-4 font-medium">{pagination?.total || clientsList.length}</span>
          </div>
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span className="px-3 text-sm text-gray-600">
                Page {currentPage} of {pagination.pages}
              </span>
              <button
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
                disabled={currentPage === pagination.pages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

            <AddClientModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {editingClient && (
        <EditClientModal
          isOpen={true}
          onClose={() => setEditingClient(null)}
          client={editingClient}
        />
      )}
    </div>
  );
};